import React, { useState } from "react";
import { useGetSystemMail, useUpdateSystemMail } from "../../hooks/mailing";
import { ErrorTryRefetch } from "../../core/ErrorTryRefetch";
import { Button, Card, CardActionArea, CardContent, Container, Dialog, DialogActions, DialogContent, Divider, Typography, Skeleton, Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ISystemMail } from "../../Interfaces/ISystemMail";
import { SystemMailEdit } from "./SystemMailEdit";
import { SaveButton } from "../../core/SaveButton";
import { useAlert } from "../../core/AlertContext";
import { BrandedDataGridOverlay } from "../../core/BrandedDataGridOverlay";
import DetailScreenHeader from "../../core/DetailScreenHeader";
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from "../../calendar/dialogLayout";
import PageHeader from '../../core/PageHeader';
import PvtaCard from '../../core/PvtaCard';

export const SystemMailOverview: React.FC = () => {
    const { systemMailArray, isLoadingSystemMailArray, refetchSystemMailArray } = useGetSystemMail();
    const [editSystemMail,setEditSystemMail] = useState<ISystemMail|null>(null);
    const { updateSystemMail, isPendingSystemMail } = useUpdateSystemMail();
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const openSystemMail = (systemMail: ISystemMail) => setEditSystemMail({
        ...systemMail,
        attachments: systemMail.attachments ?? [],
        new_attachments: [],
        delete_attachment_ids: [],
    });

    const columns: GridColDef<ISystemMail>[] = [
        { field: 'id', 
          headerName: 'Nr.', 
          width: 90 
        },
        { field: 'title', 
          headerName: 'Bezeichnung', 
          flex: 1
        },
    ]

    const onClose = () => {
        setEditSystemMail(null);
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault(); 
        if (editSystemMail !== null) {
            try {
                await updateSystemMail(editSystemMail);
                //refetchUserArray();
                showAlert({ text: 'Systemnachricht erfolgreich aktualisiert.' });
                onClose();
                refetchSystemMailArray();
            } catch (error) {
                showAlert({
                    text: 'Fehler beim Aktualisieren der Systemnachricht.',
                    severity: 'error',
                  });
                console.error( error);
            }
        }
    }

    if (isLoadingSystemMailArray) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Skeleton variant="text" width={240} height={44} />
                <Divider sx={{ my: 3 }} />
                <Stack spacing={1}>
                    <Skeleton variant="rounded" height={48} />
                    {[0, 1, 2, 3, 4].map((item) => (
                        <Skeleton key={item} variant="rounded" height={52} />
                    ))}
                </Stack>
            </Container>
        );
    }

    if (!systemMailArray) {
        return(
          <ErrorTryRefetch
            refetchFunction={refetchSystemMailArray}
          />
        )
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 0, sm: 2 } }}>
            <PageHeader title="Systemnachrichten" subtitle="Vorlagen für automatische System-E-Mails verwalten." />
            <PvtaCard large sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                {!isMobile ? (
                    <DataGrid
                        rows={systemMailArray}
                        columns={columns}
                        onRowClick={(params) => openSystemMail(params.row)}
                        autoHeight
                        slots={{
                            noRowsOverlay: BrandedDataGridOverlay,
                            noResultsOverlay: BrandedDataGridOverlay,
                        }}
                        sx={{
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                ) : (
                    <Stack spacing={1.5}>
                        {systemMailArray.map((systemMail) => (
                            <Card key={systemMail.id} variant="outlined">
                                <CardActionArea onClick={() => openSystemMail(systemMail)}>
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {systemMail.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{systemMail.id}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                Betreff: {systemMail.subject}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {systemMail.attachments?.length ?? 0} Anhang/Anhänge
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Stack>
                )}
            </PvtaCard>

            {(editSystemMail !== null) &&
                <Dialog
                    open
                    onClose={onClose}
                    maxWidth={EVENT_DIALOG_MAX_WIDTH}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={EVENT_DIALOG_PAPER_SX}
                >
                    <form onSubmit={handleSubmit}>
                        <DetailScreenHeader
                            title={editSystemMail.title || 'Systemnachricht'}
                            onClose={onClose}
                        />
                        <DialogContent sx={{ pt: 2 }}>
                            <SystemMailEdit
                                editSystemMail={editSystemMail}
                                setEditSystemMail={setEditSystemMail}
                                showHeading={false}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button variant="text" onClick={onClose}>Abbruch</Button>
                            <SaveButton
                                isPending={isPendingSystemMail}
                                type="submit"
                            />
                        </DialogActions>
                    </form>
                </Dialog>

            }
        </Container>
    )

}
